import {observer} from 'mobx-react-lite'
import React, {useCallback, useLayoutEffect, useRef} from 'react'
import {Util, UUID} from 'yougile-util'
import {Hub, noBoardContainerId} from 'yougile-model'
import {Items} from '@api/model/items'
import {addingAllowed} from '@client/hub-actions-allowed'
import {useBoardStore} from '@components/board/board-store'
import {useColumnStore} from '@components/columns/column-store'
import {
  DnDDataObject,
  DnDDataType,
  DraggableProps,
  useDnDStore
} from '@components/draggable'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'
import {taskPlaceholderMixin} from './containers'
import {TaskCard} from './task-card'

export interface Props {
  task: Hub
  index: number

  createMirrorByDrag?: boolean
  reportColumn?: boolean
}

const accepts: DnDDataType[] = ['task']
const container = '.task-group-container'

export const TaskCardDrag = observer((props: Props) => {
  const {task, index, createMirrorByDrag} = props
  const [, columnStore] = useColumnStore()
  const [boardState, boardStore] = useBoardStore()
  const group = columnStore.column

  const dndIdRef = useRef<UUID>()
  const elementRef = useRef<HTMLElement | null>()

  const [, dndStore] = useDnDStore()

  const onDrop = useCallback(
    (drag: DnDDataObject, after?: boolean) => {
      boardStore.onTaskDrop({
        drag,
        toColumn: group,
        taskId: task.id,
        after,
        columnStore
      })
    },
    [group, task.id, columnStore]
  )

  useLayoutEffect(() => {
    return () => {
      if (dndIdRef.current) {
        dndStore.removeDroppable(dndIdRef.current)
      }
    }
  }, [])

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const shouldCreateMirror = createMirrorByDrag
    const draggableProps: DraggableProps = {
      dropArea: boardState.dropArea,
      data: {
        type: 'task',
        payload: {
          taskId: task.id,
          columnId: group.id,
          shouldCreateMirror
        }
      },
      clickablesOnHover: true,
      placeholder: getPlaceholder,
      ghost: getGhost(shouldCreateMirror),
      scrollContainers: [
        {
          selector: '.proj-task-list',
          direction: 'x',
          allowOutside: true,
          thresholds: width => 0.2 * width
        },
        {selector: '.column-scroll-wrapper', direction: 'y', absolute: true}
      ],
      trigger: 'middleY',
      canBeHiddenOnDrag: !createMirrorByDrag
    }

    e.stopPropagation()
    e.preventDefault() // fixes cursor problems in Safari

    if (globals.dragAllowed(task.id)) {
      if (createMirrorByDrag) {
        if (
          addingAllowed(task.location) ||
          task.location === noBoardContainerId
        ) {
          dndStore.onMouseDown(e, draggableProps)
        }
      } else {
        dndStore.onMouseDown(e, draggableProps)
      }
      return
    }
  }, [])

  const makeRef = (element: HTMLElement) => {
    if (!element || element === elementRef.current) {
      return
    }
    elementRef.current = element
    dndIdRef.current = Util.UUID()

    dndStore.addDroppable({
      id: dndIdRef.current,
      element,
      accepts,
      onDrop,
      container,
      showPlaceholder: !createMirrorByDrag
    })
  }

  return (
    <TaskCard
      ref={makeRef}
      onMouseDown={onMouseDown}
      task={task}
      containerId={group.id}
      onClick={e => columnStore.selectMultipleTasksId(task.id, e)}
      style={{zIndex: 1000 - index}}
      className={cn(
        'proj-task-item',
        Items.isHidden(Items.get(task.id)) && 'api-hide'
      )}
      withLocationTooltip={props.reportColumn}
    />
  )
})

const getPlaceholder = (rect: DOMRect) => {
  const placeholder = document.createElement('div')
  placeholder.style.height = `${rect.height}px`
  placeholder.className = taskPlaceholderMixin
  return placeholder
}

const getGhost =
  (copy = false) =>
  (ghost: HTMLElement) => {
    ghost.classList.add('!shadow-lg')
    if (copy) {
      ghost.classList.add('!cursor-copy')
    }
    return ghost
  }
